import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import API_BASE_URL from "../config/apiConfig";
import { useAuth } from "../context/AuthProvider";

type AuditRow = {
  id: string;
  timestamp: string;
  action: "created" | "updated" | "status_updated" | "deleted" | "moved" | "copied" | string;
  action_label?: string;
  actor_user_id?: string | null;
  actor_role?: string | null;
  actor_name?: string | null;
  changes?: any;          // poate fi dict, string JSON sau text
  prev_snapshot?: any;    // poate fi dict sau string JSON
  new_snapshot?: any;     // idem
};

type StatusItem = {
  key: string;
  value: string;
  type: "therapy" | "activity";
  activity_key?: string | null;
};

interface Props {
  appointmentId: string | null;
  isOpen: boolean;
  onRequestClose: () => void;
}

const ACTION_LABEL_FALLBACK: Record<string, string> = {
  created: "Creată",
  updated: "Modificată",
  moved: "Mutată",
  deleted: "Ștearsă",
  status_updated: "Status modificat",
  copied: "Copiată",
};

/* ========== utils robuste ========== */
const pad = (n: number) => String(n).padStart(2, "0");
const roDate = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
const roTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const roDateTime = (d: Date) => `${roDate(d)} ${roTime(d)}`;

const isISO = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}T/.test(s);
const toDate = (maybe: any) => {
  if (!maybe) return null;
  const s = String(maybe);

  // ISO
  if (isISO(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // „dd.mm.yyyy hh:mm”
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})$/);
  if (m) {
    const [_, dd, mm, yyyy, HH, MM] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(HH), Number(MM));
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const fmtDateInterval = (start?: any, end?: any) => {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return "—";
  return `${roDate(s)} ${roTime(s)}–${roTime(e)}`;
};

// parsează în buclă până nu mai e string JSON
const deepParse = (v: any): any => {
  let cur = v;
  for (let i = 0; i < 3; i++) {
    if (typeof cur === "string") {
      try {
        const parsed = JSON.parse(cur);
        cur = parsed;
      } catch {
        break;
      }
    } else {
      break;
    }
  }
  return cur;
};

// uneori valorile vin ca {old,new}
const unwrap = (v: any, prefer: "new" | "old" = "new") => {
  const x = deepParse(v);
  if (x && typeof x === "object" && ("old" in x || "new" in x)) {
    return (x as any)[prefer] ?? (x as any).old ?? (x as any).new ?? "";
  }
  return x;
};

/* ===== extrage din CHANGES și din snapshots (cu fallback) ===== */
const pickFromChanges = (row: AuditRow) => {
  const raw = deepParse(row.changes);
  if (!raw) return null;

  // dict cu chei RO (formatul din backend)
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const start = raw["Început"];
    const end   = raw["Sfârșit"];
    const status = raw["Status"];

    return {
      start_old: unwrap(start, "old"),
      end_old:   unwrap(end, "old"),
      start_new: unwrap(start, "new"),
      end_new:   unwrap(end, "new"),
      status_old: unwrap(status, "old"),
      status_new: unwrap(status, "new"),
    };
  }

  // dacă e string, încercăm să extragem măcar statusul X → Y
  if (typeof raw === "string") {
    const s = raw;
    let status_old: string | null = null;
    let status_new: string | null = null;
    const mStatus = s.match(/Status[^:]*:\s*([^→\n;]+)\s*→\s*([^\n;]+)/);
    if (mStatus) {
      status_old = mStatus[1].trim();
      status_new = mStatus[2].trim();
    }
    return { start_old: null, end_old: null, start_new: null, end_new: null, status_old, status_new };
  }

  return null;
};

/* ========== component ========== */
const AppointmentHistoryModal: React.FC<Props> = ({ appointmentId, isOpen, onRequestClose }) => {
  const { token } = useAuth();

  const [items, setItems] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [statuses, setStatuses] = useState<StatusItem[]>([]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const stRes = await fetch(`${API_BASE_URL}/types/appointment-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const st = await stRes.json();
        setStatuses(st?.data || []);
      } catch {
        // ignore
      }
    })();
  }, [token]);

  // history
  useEffect(() => {
    if (!isOpen || !appointmentId) return;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const r = await fetch(`${API_BASE_URL}/appointments/${appointmentId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          const { detail } = await r.json().catch(() => ({}));
          throw new Error(detail || "Eroare la încărcarea istoricului.");
        }
        const data = await r.json();
        setItems((data?.data || []) as AuditRow[]);
      } catch (e: any) {
        setErr(e?.message || "Eroare la încărcarea istoricului.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, appointmentId, token]);

  // status label simplu (fără context tip/activitate – pentru modal vrem doar textul)
  const statusLabel = (key?: string | null): string => {
    if (!key) return "—";
    const any = statuses.find(s => s.key === key);
    return any?.value || key;
  };

  /* ===== helpers pt intervale ===== */
  const intervalFromSnapshot = (snap: any, prefer: "new" | "old") => {
    if (!snap) return null;
    const s = unwrap(snap.start_time, prefer);
    const e = unwrap(snap.end_time, prefer);
    return (s && e) ? fmtDateInterval(s, e) : null;
  };

  const intervals = (row: AuditRow) => {
    const prev = deepParse(row.prev_snapshot);
    const cur  = deepParse(row.new_snapshot);
    const ch   = pickFromChanges(row);

    const oldI = intervalFromSnapshot(prev, "old") || fmtDateInterval(ch?.start_old, ch?.end_old);
    const newI = intervalFromSnapshot(cur, "new")  || fmtDateInterval(ch?.start_new, ch?.end_new);
    return { oldI, newI };
  };

  const statusesPair = (row: AuditRow) => {
    const ch = pickFromChanges(row);
    const oldK = ch?.status_old ?? unwrap(deepParse(row.prev_snapshot)?.status, "old");
    const newK = ch?.status_new ?? unwrap(deepParse(row.new_snapshot)?.status, "new");
    return { oldS: statusLabel(oldK), newS: statusLabel(newK) };
  };

  /* ===== renderer simplificat ===== */
  const renderDetails = (row: AuditRow) => {
    const label = row.action_label || ACTION_LABEL_FALLBACK[row.action] || row.action;
    const actorShown = row.actor_name || row.actor_user_id || "—";

    const { oldI, newI } = intervals(row);
    const { oldS, newS } = statusesPair(row);

    let text = "";
    switch (row.action) {
      case "created":
        text = `Creată: ${newI || "—"}`;
        break;
      case "deleted":
        text = `Ștearsă: ${oldI || "—"}`;
        break;
      case "copied":
        text = `Copiată: din ${oldI || "—"} în ${newI || "—"}`;
        break;
      case "updated":
      case "moved":
        if (oldI && newI && oldI !== newI) {
          text = `Mutată: de la ${oldI} la ${newI}`;
        } else {
          // alte modificări (dar păstrăm simplu)
          text = `Modificată: ${newI || oldI || "—"}`;
        }
        break;
      case "status_updated":
        text = `Status: ${oldS} → ${newS}`;
        break;
      default:
        // fallback ultra-simplu
        if (oldI || newI) {
          text = `${label}: ${newI || oldI}`;
        } else {
          text = label;
        }
        break;
    }

    return (
      <div key={row.id} className="timeline-item">
        <div className="when">{roDateTime(new Date(row.timestamp))}</div>
        <div className="what">
          <strong className="action">{label}</strong>
          <div className="by">de {row.actor_role || "utilizator"} ({actorShown})</div>
          <div className="changes-text">{text}</div>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} contentLabel="Istoric programare">
      <h2 className="modal-title">Istoric programare</h2>

      {loading && <p>Se încarcă…</p>}
      {err && !loading && <p style={{ color: "crimson" }}>{err}</p>}

      {!loading && !err && (
        items.length === 0 ? (
          <p>Nu există înregistrări.</p>
        ) : (
          <div className="audit-timeline">
            {items.map(renderDetails)}
          </div>
        )
      )}

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button className="primary-btn" onClick={onRequestClose}>Închide</button>
      </div>

      <style>{`
        .audit-timeline { display:flex; flex-direction:column; gap:12px; max-height:60vh; overflow:auto; }
        .timeline-item { border-left:3px solid #6C298B; padding:8px 12px; background:#faf7fc; border-radius:8px; }
        .when { font-size: 0.9rem; color:#666; }
        .what { margin-top:4px; }
        .action { color:#6C298B; }
        .by { font-size:0.9rem; color:#777; margin-top:2px; }
        .changes-text { margin-top:6px; white-space:pre-wrap; font-size:0.95rem; }
      `}</style>
    </Modal>
  );
};

export default AppointmentHistoryModal;